/**
 * Deep Link Utilities
 * Convert web URLs to app deep links and handle navigation
 */

const APP_SCHEME = 'playstoreapp://';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.probwebsolutions.reactstore';

/**
 * Route mapping between web paths and app screens
 */
const ROUTE_MAPPING = {
    '/': 'home',
    '/apps': 'apps',
    '/games': 'games',
    '/blogs': 'blogs',
    '/profile': 'profile',
    '/wishlist': 'wishlist',
    '/search': 'search',
    '/my-apps': 'profile',
    '/settings': 'profile',

    // App routes
    '/app/details': 'app/details',
    '/apps/category': 'apps/category',
    '/apps/recommended': 'apps/recommended',
    '/apps/top-charts': 'apps/top-charts',
    '/apps/latest': 'apps/latest',
    '/apps/categories': 'apps/categories',

    // Game routes
    '/games/category': 'games/category',
    '/games/recommended': 'games/recommended',
    '/games/top-charts': 'games/top-charts',
    '/games/latest': 'games/latest',
    '/games/categories': 'games/categories',

    // Blog routes
    '/blog': 'blog',
    '/blogs/category': 'blogs/category',
    '/blogs/author': 'blogs/author',
    '/blogs/tag': 'blogs/tag',
    '/blogs/featured': 'blogs/featured',
    '/blogs/popular': 'blogs/popular',
    '/blogs/latest': 'blogs/latest',
    '/blogs/categories': 'blogs/categories',

    // Other routes
    '/collection': 'collection',
    '/developer': 'developer',
};

/**
 * Convert web path to app deep link
 * @param {string} webPath - Current web path (e.g., '/app/details/whatsapp')
 * @returns {string} Deep link URL
 */
export const webPathToDeepLink = (webPath) => {
    // Remove leading slash
    const path = webPath.startsWith('/') ? webPath.substring(1) : webPath;

    // If empty path, return home
    if (!path) {
        return `${APP_SCHEME}home`;
    }

    // Find matching route
    for (const [webRoute, appRoute] of Object.entries(ROUTE_MAPPING)) {
        const webRoutePattern = webRoute.substring(1); // Remove leading slash

        if (path === webRoutePattern) {
            return `${APP_SCHEME}${appRoute}`;
        }

        // Check if path starts with the route (for dynamic segments)
        if (path.startsWith(webRoutePattern + '/')) {
            const params = path.substring(webRoutePattern.length + 1);
            return `${APP_SCHEME}${appRoute}/${params}`;
        }
    }

    // Default: convert path as-is
    return `${APP_SCHEME}${path}`;
};

/**
 * Get current page deep link
 * @returns {string} Deep link for current page
 */
export const getCurrentPageDeepLink = () => {
    const currentPath = window.location.pathname;
    return webPathToDeepLink(currentPath);
};

/**
 * Open app with deep link
 * Falls back to Play Store if app is not installed
 * @param {string} deepLink - Deep link URL
 * @param {number} timeout - Timeout in ms to wait before fallback (default: 2500)
 * @returns {Promise<boolean>} - Returns true if app likely opened, false otherwise
 */
export const openApp = (deepLink, timeout = 2500) => {
    return new Promise((resolve) => {
        const startTime = Date.now();
        let appOpened = false;

        // Create a hidden iframe to attempt opening the app
        // This is more reliable than window.location for custom schemes
        const iframe = document.createElement('iframe');
        iframe.style.display = 'none';
        iframe.src = deepLink;
        document.body.appendChild(iframe);

        // Clean up iframe after a short delay
        setTimeout(() => {
            document.body.removeChild(iframe);
        }, 1000);

        // Also try window.location as backup
        setTimeout(() => {
            if (!appOpened) {
                window.location.href = deepLink;
            }
        }, 100);

        // Check if page becomes hidden (app opened)
        const handleVisibilityChange = () => {
            if (document.hidden) {
                appOpened = true;
                clearTimeout(fallbackTimer);
                resolve(true);
            }
        };

        // Check if user navigated away (app opened)
        const handleBlur = () => {
            appOpened = true;
            clearTimeout(fallbackTimer);
            resolve(true);
        };

        // Set up fallback timer
        const fallbackTimer = setTimeout(() => {
            const elapsedTime = Date.now() - startTime;

            // If user is still on the page after timeout, app likely not installed
            if (!document.hidden && !appOpened && elapsedTime >= timeout - 100) {
                console.log('App not installed or failed to open');
                resolve(false);
            } else {
                resolve(true);
            }

            // Cleanup listeners
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('blur', handleBlur);
        }, timeout);

        // Listen for visibility and blur events
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('blur', handleBlur);
    });
};

/**
 * Open current page in app
 * @returns {Promise<boolean>} - Returns true if app likely opened, false otherwise
 */
export const openCurrentPageInApp = async () => {
    const deepLink = getCurrentPageDeepLink();
    return await openApp(deepLink);
};

/**
 * Get Play Store URL
 * @returns {string}
 */
export const getPlayStoreUrl = () => {
    return PLAY_STORE_URL;
};

const deepLinkUtils = {
    webPathToDeepLink,
    getCurrentPageDeepLink,
    openApp,
    openCurrentPageInApp,
    getPlayStoreUrl,
};

export default deepLinkUtils;
